import { useMemo, useState } from 'react';

import { Stack } from '@mui/material';

import { useQuery } from '@redwoodjs/web';

import { SageConnection } from 'src/components';
import Loader from 'src/components/Loader/Loader';
import { EditRegisteredAddressModal } from 'src/components/Modals';
import ReusableGrid from 'src/components/ReusableGrid';
import Status from 'src/components/Status/Status';
import { showErrorToast } from 'src/components/Toasts/toast';
import { GET_MERCHANT_DASHBOARD } from 'src/graphql/queries/merchants.query';
import { useCreateMerchantSageConnection, useCreateNote } from 'src/hooks';
import { useCurrency } from 'src/hooks/useCurrency';
import useMerchantId from 'src/hooks/useMerchantId';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { PageHeaderVariant } from 'src/layouts/MainLayout/PageHeader';

import Notes from '../ViewPartnerPage/components/Notes/Notes';
import OutstandingInvoices from '../ViewPartnerPage/components/OutstandingInvoices/OutstandingInvoices';
import PaymentDetails from '../ViewPartnerPage/components/PaymentDetails/PaymentDetails';
import CheckinCallHistory from '../ViewServicePage/components/CheckinCallHistory';
import OutletSection from '../ViewServicePage/components/OutletSection';

import AddNoteModal from './components/AddNoteModal/AddNoteModal';
import FlexRow from './components/FlexRow/FlexRow';
import History from './components/History/History';
import PrincipalTable from './components/PrincipalTable';
import RegisteredAddress from './components/RegisteredAddress/RegisteredAddress';
import Services from './components/Services/Services';
import ViewCheckinCallModal from './components/ViewCheckinCallModalProps/ViewCheckinCallModalProps';
import formatMerchantName from './utils/utils';

interface ViewMerchantPageProps {
    merchantAccountNumber: string;
}

function ViewMerchantPage({ merchantAccountNumber }: ViewMerchantPageProps): JSX.Element {
    const [viewPaymentDetails, setViewPaymentDetails] = useState(false);

    const { id } = useMerchantId(merchantAccountNumber);

    const [isCheckinCallModalOpen, setIsCheckinCallModalOpen] = useState(false);

    const [selectedCheckInCallNote, setSelectedCheckInCallNote] = useState(null);

    const [openCreateNoteModal, setOpenCreateNoteModal] = useState(false);

    const [isEditAddressModalOpen, setIsEditAddressModalOpen] = useState(false);

    const { loading, data, refetch } = useQuery(GET_MERCHANT_DASHBOARD, {
        variables: { id },

        skip: !id,

        onError: (error) => {
            return showErrorToast(error.message);
        },
    });

    const { currencySymbol } = useCurrency(
        data?.merchantDashboard?.application?.partner?.countryDetails
    );

    const { onCreateNote } = useCreateNote({
        merchantId: id,

        onRefetch: refetch,
    });

    const { onCreateMerchantSageConnection } = useCreateMerchantSageConnection({
        merchantId: data?.merchantDashboard?.id,

        onRefetch: refetch,
    });

    const toggleCreateNoteModal = () => {
        setOpenCreateNoteModal(!openCreateNoteModal);
    };

    const handleCreateNote = (values: { description: string; subject: string }) => {
        onCreateNote(values);

        toggleCreateNoteModal();
    };

    let { merchantDashboard } = data ?? {};

    merchantDashboard = {
        ...merchantDashboard,

        notes: merchantDashboard?.notes?.map((note) => {
            return {
                ...note,

                createdById: note?.createdById,

                createdBy: `${note?.createdBy?.firstName} ${note?.createdBy?.lastName}`,
            };
        }),
    };

    const bankDetails = useMemo(() => {
        return {
            bic: merchantDashboard?.bic ?? '',

            iban: merchantDashboard?.iban ?? '',

            accountNumber: merchantDashboard?.accountNumber ?? '',

            sortCode: merchantDashboard?.sortCode ?? '',
        };
    }, [merchantDashboard]);

    const merchantLegalName = merchantDashboard?.legalName;

    const merchantTradingName = merchantDashboard?.tradingName;

    const sageContactId = merchantDashboard?.sageContactId;

    const subHeadingDetails = sageContactId
        ? `${merchantAccountNumber} / Sage Contact ID: ${sageContactId}`
        : merchantAccountNumber;

    const merchantDisplayName = formatMerchantName(merchantLegalName, merchantTradingName);

    const openCheckinCallModal = (dateTime, subject, addedBy, id, body) => {
        setSelectedCheckInCallNote({ dateTime, subject, addedBy, id, body });

        setIsCheckinCallModalOpen(true);
    };

    const closeCheckinCallModal = () => {
        setSelectedCheckInCallNote(null);

        setIsCheckinCallModalOpen(false);
    };

    if (loading || !data) return <Loader />;

    return (
        <PageContent
            headerVariant={PageHeaderVariant.DASHBOARD}
            heading={merchantDisplayName}
            subHeading={subHeadingDetails}
            callToActions={
                <Stack
                    width="100%"
                    direction="row"
                    spacing={2}
                    justifyContent="end"
                    alignItems="end"
                >
                    <SageConnection
                        sageContactId={sageContactId}
                        onRefresh={onCreateMerchantSageConnection}
                    />
                    <Status
                        status={merchantDashboard?.status}
                        id={merchantDashboard?.id}
                        isMerchant
                    />
                </Stack>
            }
        >
            <FlexRow>
                <OutletSection
                    outlets={merchantDashboard?.application?.merchantInformation?.outlets}
                    useFullWidth
                    useEdit
                />
            </FlexRow>
            <FlexRow gap={2} item>
                <RegisteredAddress
                    registeredAddress={
                        merchantDashboard?.application?.merchantInformation?.aboutBusiness
                            ?.registeredAddress
                    }
                    onEdit={() => {
                        return setIsEditAddressModalOpen(true);
                    }}
                />
            </FlexRow>
            <FlexRow item>
                <PrincipalTable merchant={merchantDashboard} refetchMerchant={refetch} />
            </FlexRow>
            <FlexRow gap={2} item>
                <ReusableGrid
                    xs={12}
                    title="Payment Details"
                    hasView
                    setViewPaymentDetails={setViewPaymentDetails}
                >
                    <PaymentDetails bank={bankDetails} viewPaymentDetails={viewPaymentDetails} />
                </ReusableGrid>

                <ReusableGrid xs={12} title="Outstanding Invoices">
                    <OutstandingInvoices
                        invoices={merchantDashboard.invoices || []}
                        usersCurrency={currencySymbol}
                    />
                </ReusableGrid>
            </FlexRow>

            <FlexRow item>
                <Services
                    merchantTradingName={merchantTradingName ?? merchantLegalName}
                    services={merchantDashboard?.services}
                    merchantId={id}
                />
            </FlexRow>

            <FlexRow item>
                <CheckinCallHistory merchantId={id} onOpen={openCheckinCallModal} />
            </FlexRow>

            <FlexRow gap={2} item>
                <ReusableGrid
                    title="Notes"
                    addRecordButtonConfig={{
                        addRecordButtonText: 'Add Note',

                        addRecordCallBack: toggleCreateNoteModal,
                    }}
                >
                    <Notes notes={merchantDashboard?.notes} refetch={refetch} isMerchant />
                </ReusableGrid>

                <ReusableGrid title="History">
                    <History auditLogs={merchantDashboard?.auditLog || []} />
                </ReusableGrid>
            </FlexRow>

            <AddNoteModal
                open={openCreateNoteModal}
                onClose={toggleCreateNoteModal}
                onSubmit={handleCreateNote}
            />

            <ViewCheckinCallModal
                open={isCheckinCallModalOpen}
                onClose={closeCheckinCallModal}
                checkInCallNote={selectedCheckInCallNote}
            />

            <EditRegisteredAddressModal
                isOpen={isEditAddressModalOpen}
                onClose={() => {
                    return setIsEditAddressModalOpen(false);
                }}
                applicationId={merchantDashboard?.application?.id}
                merchantInformation={merchantDashboard?.application?.merchantInformation}
                onUpdate={() => {
                    refetch();
                }}
            />
        </PageContent>
    );
}

export default ViewMerchantPage;
